import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import PageTitle from '../shared/PageTitle';

// Floating Action Button for Switch User Management
export const SwitchUserFAB = () => {
    const { currentUser } = useContext(AuthContext);
    const navigate = useNavigate();

    if (!currentUser?.isSwitchedUser) {
        return null;
    }

    return (
        <div
            className="switch-user-fab"
            onClick={() => navigate('/superadmin/switch-user')}
            title="Switch User Management"
        >
            <i className="fas fa-exchange-alt"></i>
            <span className="fab-tooltip">Switch User</span>
        </div>
    );
};

// Notification Banner for Switch User Status
export const SwitchUserBanner = () => {
    const { currentUser } = useContext(AuthContext);
    const navigate = useNavigate();

    if (!currentUser?.isSwitchedUser) {
        return null;
    }

    return (
        <div className="switch-user-banner">
            <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <i className="fas fa-user-secret mr-2"></i>
                        <strong>Switched User Mode:</strong> You are currently logged in as {currentUser.name} ({currentUser.role})
                        {currentUser.organization_name && ` from ${currentUser.organization_name}`}
                    </div>
                    <div>
                        <button
                            className="btn btn-sm btn-outline-light"
                            onClick={() => navigate('/superadmin/switch-user')}
                        >
                            <i className="fas fa-exchange-alt mr-1"></i>
                            Manage Switch
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SwitchUser = () => {
    const { currentUser, login } = useContext(AuthContext);
    const navigate = useNavigate();
    const [switchableUsers, setSwitchableUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [switchingBack, setSwitchingBack] = useState(false);
    const [showUserList, setShowUserList] = useState(false);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [organizations, setOrganizations] = useState([]);

    // Pagination state and logic
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Filter users based on search term and organization
    const filteredUsers = switchableUsers.filter(user => {
        const matchesSearch = searchTerm === '' ||
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesOrganization = selectedOrganization === '' ||
            user.organization_name === selectedOrganization;

        return matchesSearch && matchesOrganization;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const previousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedOrganization]);

    // Extract unique organizations for filter dropdown
    useEffect(() => {
        const uniqueOrgs = [...new Set(switchableUsers.map(user => user.organization_name).filter(Boolean))];
        setOrganizations(uniqueOrgs);
    }, [switchableUsers]);

    useEffect(() => {
        // Allow superadmin or switched users to access this page
        if (currentUser && currentUser.role !== 'superadmin' && !currentUser.isSwitchedUser) {
            navigate('/login');
            return;
        }

        fetchSwitchableUsers();
    }, [currentUser, navigate]);

    const fetchSwitchableUsers = async () => {
        try {
            setLoading(true);
            console.log('🔍 Fetching switchable users...');
            console.log('Current user:', currentUser);

            const response = await authAPI.getSwitchableUsers();
            console.log('Switchable users response:', response);

            setSwitchableUsers(response.data.users || []);
            console.log('Switchable users set:', response.data.users || []);
        } catch (error) {
            console.error('Error fetching switchable users:', error);
            console.error('Error response:', error.response?.data);
            toast.error('Failed to load switchable users');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchToUser = async (userId) => {
        try {
            setSwitching(true);
            const response = await authAPI.switchToUser(userId);

            // Update token and user data
            localStorage.setItem('token', response.data.token);
            sessionStorage.setItem('token', response.data.token);
            sessionStorage.setItem('user', JSON.stringify(response.data.user));

            // Update auth context
            await login(response.data.token);

            toast.success(response.data.message);

            // Redirect to appropriate dashboard based on user role
            const userRole = response.data.user.role;
            const orgDomain = response.data.user.organization_domain;

            let dashboardPath;
            switch (userRole) {
                case 'admin':
                    dashboardPath = orgDomain ? `/${orgDomain}/admin/dashboard` : '/admin/dashboard';
                    break;
                case 'owner':
                    dashboardPath = orgDomain ? `/${orgDomain}/owner/dashboard` : '/owner/dashboard';
                    break;
                case 'guard':
                    dashboardPath = orgDomain ? `/${orgDomain}/guard/dashboard` : '/guard/dashboard';
                    break;
                case 'maintenance':
                    dashboardPath = orgDomain ? `/${orgDomain}/maintenance/dashboard` : '/maintenance/dashboard';
                    break;
                default:
                    dashboardPath = '/landing';
            }

            navigate(dashboardPath);
        } catch (error) {
            console.error('Error switching to user:', error);
            toast.error(error.response?.data?.message || 'Failed to switch to user');
        } finally {
            setSwitching(false);
        }
    };

    const handleSwitchBack = async () => {
        try {
            setSwitchingBack(true);
            const response = await authAPI.switchBack();

            // Update token and user data
            localStorage.setItem('token', response.data.token);
            sessionStorage.setItem('token', response.data.token);
            sessionStorage.setItem('user', JSON.stringify(response.data.user));

            // Update auth context
            await login(response.data.token);

            toast.success(response.data.message);

            // Redirect back to superadmin dashboard
            navigate('/superadmin/dashboard');
        } catch (error) {
            console.error('Error switching back:', error);
            toast.error(error.response?.data?.message || 'Failed to switch back');
        } finally {
            setSwitchingBack(false);
        }
    };

    // Check if current user is a switched user
    const isSwitchedUser = currentUser?.isSwitchedUser;

    console.log('🔍 SwitchUser component render:');
    console.log('- Current user:', currentUser);
    console.log('- Is switched user:', isSwitchedUser);
    console.log('- Loading:', loading);
    console.log('- Switchable users count:', switchableUsers.length);
    console.log('- Show user list:', showUserList);
    console.log('- Filtered users count:', filteredUsers.length);
    console.log('- Current page:', currentPage);
    console.log('- Total pages:', totalPages);

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <PageTitle
                    icon="fa-exchange-alt"
                    title="Switch User"
                    className="mb-4"
                />
                <div className="text-center my-5">
                    <div className="spinner-border" role="status" style={{ color: '#1F458E' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <PageTitle
                icon="fa-exchange-alt"
                title="Switch User"
                className="mb-4"
            />

            {isSwitchedUser ? (
                // Show switch back option if currently switched to another user
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow mb-4">
                            <div className="card-header bg-warning text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-user-secret mr-2"></i>
                                    Currently Switched User
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="alert alert-info">
                                    <strong>Current User:</strong> {currentUser.name} ({currentUser.email})<br />
                                    <strong>Role:</strong> {currentUser.role}<br />
                                    <strong>Organization:</strong> {currentUser.organization_name || 'N/A'}
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSwitchBack}
                                        disabled={switchingBack}
                                    >
                                        {switchingBack ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                Switching Back...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-arrow-left mr-2"></i>
                                                Switch Back to Superadmin
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowUserList(true)}
                                    >
                                        <i className="fas fa-exchange-alt mr-2"></i>
                                        Switch to Different User
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Show user list if superadmin or if switching to different user */}
            {(currentUser?.role === 'superadmin' || (isSwitchedUser && showUserList)) && (
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow">
                            <div className="superadmin-section d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <i className="fas fa-users mr-2"></i>
                                    {isSwitchedUser ? 'Switch to Different User' : 'Switch to User'}
                                </h6>
                                <div className="d-flex gap-2">
                                    {isSwitchedUser && (
                                        <button
                                            className="btn btn-outline-light btn-sm"
                                            onClick={() => setShowUserList(false)}
                                            title="Hide user list"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search and Filter Controls */}
                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <div className="form-group mb-0">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="search_input"
                                                placeholder="Search by name, email, or organization..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group mb-0">
                                            <select
                                                className="form-control"
                                                id="organization_filter"
                                                value={selectedOrganization}
                                                onChange={(e) => setSelectedOrganization(e.target.value)}
                                            >
                                                <option value="">All Organizations</option>
                                                {organizations.map((org, index) => (
                                                    <option key={index} value={org}>
                                                        {org}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4 d-flex align-items-center">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setSearchTerm('');
                                                setSelectedOrganization('');
                                            }}
                                        >
                                            <i className="fas fa-times mr-2"></i>
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>

                                {switchableUsers.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="fas fa-users fa-3x text-muted mb-3"></i>
                                        <h5>No Switchable Users</h5>
                                        <p className="text-muted">
                                            No active users from active organizations found.
                                        </p>
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={fetchSwitchableUsers}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                            ) : (
                                                <i className="fas fa-sync-alt mr-2"></i>
                                            )}
                                            Refresh
                                        </button>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="fas fa-search fa-3x text-muted mb-3"></i>
                                        <h5>No Users Found</h5>
                                        <p className="text-muted">
                                            No users match your current search criteria.
                                        </p>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => {
                                                setSearchTerm('');
                                                setSelectedOrganization('');
                                            }}
                                        >
                                            <i className="fas fa-times mr-2"></i>
                                            Clear Filters
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        <th>Organization</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentItems.map((user) => (
                                                        <tr key={user.id}>
                                                            <td>
                                                                <strong>{user.name}</strong>
                                                                {isSwitchedUser && currentUser.id === user.id && (
                                                                    <span className="badge bg-info ml-2">Current</span>
                                                                )}
                                                            </td>
                                                            <td>{user.email}</td>
                                                            <td>
                                                                <span className={`badge bg-${getRoleBadgeColor(user.role)}`}>
                                                                    {user.role}
                                                                </span>
                                                            </td>
                                                            <td>{user.organization_name || 'N/A'}</td>
                                                            <td>
                                                                {isSwitchedUser && currentUser.id === user.id ? (
                                                                    <span className="text-muted">Current User</span>
                                                                ) : (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => handleSwitchToUser(user.id)}
                                                                        disabled={switching}
                                                                        title={`Switch to ${user.name}`}
                                                                    >
                                                                        {switching ? (
                                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                        ) : (
                                                                            <>
                                                                                <i className="fas fa-sign-in-alt mr-1"></i>
                                                                                Switch
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div>
                                                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
                                                </div>
                                                <nav aria-label="Switch users pagination">
                                                    <ul className="pagination mb-0">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                                                                <i className="fas fa-chevron-left"></i>
                                                            </button>
                                                        </li>
                                                        {Array.from({ length: totalPages }).map((_, index) => {
                                                            const pageNumber = index + 1;
                                                            if (
                                                                pageNumber === 1 ||
                                                                pageNumber === totalPages ||
                                                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                                            ) {
                                                                return (
                                                                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                                                        <button className="page-link" onClick={() => paginate(pageNumber)}>
                                                                            {pageNumber}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            }
                                                            if (
                                                                (pageNumber === 2 && currentPage > 3) ||
                                                                (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                                                            ) {
                                                                return (
                                                                    <li key={pageNumber} className="page-item disabled">
                                                                        <span className="page-link">...</span>
                                                                    </li>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                                                                <i className="fas fa-chevron-right"></i>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to get badge color based on role
const getRoleBadgeColor = (role) => {
    switch (role) {
        case 'admin':
            return 'primary';
        case 'owner':
            return 'success';
        case 'guard':
            return 'warning';
        case 'maintenance':
            return 'info';
        default:
            return 'secondary';
    }
};

export default SwitchUser; 